import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { ConfirmProvider } from "./components/ConfirmDialog";
import { Toaster } from "./components/ui/sonner";
import Shell from "./components/Shell";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import QuoteBuilderPage from "./pages/QuoteBuilderPage";
import QuoteListPage from "./pages/QuoteListPage";
import QuoteDetailPage from "./pages/QuoteDetailPage";
import SurveysPage from "./pages/SurveysPage";
import SurveyBuilderPage from "./pages/SurveyBuilderPage";
import CatalogPage from "./pages/CatalogPage";
import UsersPage from "./pages/UsersPage";
import ProfilePage from "./pages/ProfilePage";

const PAGE_PATHS = [["quotes", "/quotes"], ["quote_create", "/quotes/new"], ["surveys", "/surveys"], ["users", "/users"], ["profile", "/profile"]];

function Protected({ children, adminOnly = false, page }) {
  const { user, checking } = useAuth();
  const loc = useLocation();
  if (checking) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  const isAdmin = user.role === "admin";
  const pages = user.pages || [];
  if ((adminOnly && !isAdmin) || (page && !isAdmin && !pages.includes(page))) {
    const home = PAGE_PATHS.find(([k]) => pages.includes(k));
    if (home && home[1] !== loc.pathname) return <Navigate to={home[1]} replace />;
    return <Shell><div className="p-10 text-center text-slate-500" data-testid="no-page-access">Your role doesn't have access to any pages yet — please ask an admin.</div></Shell>;
  }
  return <Shell>{children}</Shell>;
}

function PublicOnly({ children }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (user) return <Navigate to="/quotes" replace />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ConfirmProvider>
          <Routes>
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/" element={<Navigate to="/quotes" replace />} />
            <Route path="/quotes" element={<Protected page="quotes"><QuoteListPage /></Protected>} />
            <Route path="/quotes/new" element={<Protected page="quote_create"><QuoteBuilderPage /></Protected>} />
            <Route path="/quotes/:id" element={<Protected page="quotes"><QuoteDetailPage /></Protected>} />
            <Route path="/quotes/:id/edit" element={<Protected page="quotes"><QuoteBuilderPage /></Protected>} />
            <Route path="/surveys" element={<Protected page="surveys"><SurveysPage /></Protected>} />
            <Route path="/surveys/new" element={<Protected page="surveys"><SurveyBuilderPage /></Protected>} />
            <Route path="/surveys/edit" element={<Protected page="surveys"><SurveyBuilderPage /></Protected>} />
            <Route path="/users" element={<Protected page="users"><UsersPage /></Protected>} />
            <Route path="/catalog" element={<Protected adminOnly><CatalogPage /></Protected>} />
            <Route path="/profile" element={<Protected page="profile"><ProfilePage /></Protected>} />
            <Route path="*" element={<Navigate to="/quotes" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </ConfirmProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
